/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'helpers/store'
import { closeSecondaryModal } from 'helpers/actions'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import UiDatePicker from 'components/Ui/UiDatePicker'
import UiTimePicker from 'components/Ui/UiTimePicker'
import SellerAction from 'store/seller/SellerAction'
import { toast } from 'utils/toastUtils'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Dates from 'utils/Dates'
import { DATE_FORMATS } from 'constants/DateFormats'

const rebookingSchema = z.object({
    date: z.date(),
    time: z.date().refine((value) => !Number.isNaN(value.getTime()), 'Invalid time format'),
})

type RebookingFormData = z.infer<typeof rebookingSchema>

const toTimeDate = (time: string): Date => {
    const match = time.match(/(?:^|T|\s)([01]?\d|2[0-3]):([0-5]\d)/)
    const hours = Number(match?.[1] ?? 12)
    const minutes = Number(match?.[2] ?? 0)

    // The picker only needs a clock value. Avoid parsing the reservation date,
    // which can shift a time-only value when the browser applies a timezone.
    return new Date(2000, 0, 1, hours, minutes, 0, 0)
}

interface BookingSellerRebookingModalProps {
    reservationId: number
    currentDate: string | Date
    currentTime: string
    onSuccess?: () => void
}

const BookingSellerRebookingModal: React.FC<
    BookingSellerRebookingModalProps
> = ({ reservationId, currentDate, currentTime, onSuccess }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const {
        control,
        handleSubmit,
        formState: { isSubmitting, errors },
    } = useForm<RebookingFormData>({
        resolver: zodResolver(rebookingSchema),
        defaultValues: {
            date: new Date(currentDate),
            time: toTimeDate(currentTime || '12:00'),
        },
    })

    const onSubmit = async (data: RebookingFormData) => {
        try {
            await dispatch(
                SellerAction.fetchSetReservationRebooking({
                    reservation_id: reservationId,
                    date: Dates.dayjs(data.date).format(DATE_FORMATS.DATE_YMD),
                    time: Dates.dayjs(data.time).format(DATE_FORMATS.TIME),
                })
            )
            toast.success(t('Reservation rebooked successfully'))
            dispatch(closeSecondaryModal())
            if (onSuccess) onSuccess()
        } catch (e) {
            console.error('Error rebooking:', e)
            toast.error(t('Failed to rebook reservation'))
        }
    }

    const onError = () => {
        toast.error(t('Please fix validation errors'))
    }

    return (
        <UiFormModalShell title={t('Rebook Reservation')}>
            <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="h-full flex flex-col"
            >
                <div className="p-4 flex-1">
                    <UiFormInner title={t('New Date & Time')}>
                        <UiFormGrid cols={2}>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t('Date')}
                                </label>
                                <Controller
                                    name="date"
                                    control={control}
                                    render={({ field }) => (
                                        <UiDatePicker
                                            value={field.value}
                                            onChange={(date: Date | null) =>
                                                date && field.onChange(date)
                                            }
                                        />
                                    )}
                                />
                                {errors.date && (
                                    <span className="text-xs text-red-500">
                                        {errors.date.message}
                                    </span>
                                )}
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t('Time')}
                                </label>
                                <Controller
                                    name="time"
                                    control={control}
                                    render={({ field }) => (
                                        <UiTimePicker
                                            value={field.value}
                                            onChange={(val) =>
                                                field.onChange(val)
                                            }
                                        />
                                    )}
                                />
                                {errors.time && (
                                    <span className="text-xs text-red-500">
                                        {errors.time.message}
                                    </span>
                                )}
                            </div>
                        </UiFormGrid>
                    </UiFormInner>
                </div>
                <UiFormActionsBar
                    primaryText={t('Confirm Rebooking')}
                    loading={isSubmitting}
                    onPrimary={handleSubmit(onSubmit, onError)}
                />
            </form>
        </UiFormModalShell>
    )
}

export default BookingSellerRebookingModal
